import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * The type roles defined in tailwind-preset.cjs. Kept in step by hand — if you
 * add a role there, add it here.
 */
const TYPE_ROLES = [
  'display',
  'h1',
  'h2',
  'h3',
  'h4',
  'body-lg',
  'body-lg-strong',
  'body',
  'body-strong',
  'body-sm',
  'body-sm-strong',
  'label',
  'caption',
  'caption-strong',
  'overline',
  'code',
];

const CONTROL_SIZES = [
  'control-xs',
  'control-sm',
  'control-md',
  'control-lg',
  'control-xl',
];
const ICON_SIZES = ['icon-xs', 'icon-sm', 'icon-md', 'icon-lg', 'icon-xl'];

/**
 * tailwind-merge only knows Tailwind's built-in scales, so the token-backed
 * ones added by the preset have to be declared here or it guesses wrong.
 *
 * The damaging case is the type scale: tailwind-merge decides `text-*` is a
 * font size only if the value looks like a t-shirt size. "h2" does not, so it
 * files `text-h2` under text *colour* — and then drops it the moment a real
 * colour class follows. `<Text variant="h2" tone="muted">` silently loses its
 * font size entirely. There is a regression test for this in the admin app's
 * library tests.
 *
 * Keep this list in step with the `extend` block in tailwind-preset.cjs.
 */
export const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [{ text: TYPE_ROLES }],
      h: [{ h: [...CONTROL_SIZES, ...ICON_SIZES] }],
      w: [{ w: [...CONTROL_SIZES, ...ICON_SIZES] }],
      size: [{ size: [...CONTROL_SIZES, ...ICON_SIZES] }],
      'min-h': [{ 'min-h': CONTROL_SIZES }],
      rounded: [{ rounded: ['pill', 'control'] }],
      shadow: [
        { shadow: ['e0', 'e1', 'e2', 'e3', 'float', 'sheet', 'control'] },
      ],
      'backdrop-blur': [{ 'backdrop-blur': ['glass'] }],
      duration: [{ duration: ['instant', 'fast', 'base', 'slow'] }],
      'ring-w': [{ ring: ['focus'] }],
    },
  },
});

/**
 * Join class names, resolving Tailwind conflicts so the last one wins.
 *
 * Use this everywhere instead of template strings. It is what lets a component
 * accept a `className` override that actually takes effect:
 *
 *   cn('px-4 bg-primary', className)
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
